import React from 'react';
import './SectionHeading.css';

export type HeadingAlignment = 'left' | 'center';

export interface SectionHeadingProps {
  /** Small eyebrow text above the heading (optional) */
  eyebrow?: string;
  /** Main heading content */
  heading: React.ReactNode;
  /** Subtitle or description paragraph (optional) */
  subtitle?: React.ReactNode;
  /** Text alignment */
  align?: HeadingAlignment;
  /** Use light text for dark backgrounds */
  dark?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Heading level (h1, h2, h3, etc.) */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Beautiful section heading component with eyebrow, main heading, and optional subtitle.
 * Features sophisticated typography with generous spacing and Apple-level refinement.
 *
 * @example
 * <SectionHeading
 *   eyebrow="Our Heritage"
 *   heading="Six Generations of Excellence"
 *   subtitle="Since 1947, the Mims family has been painting Long Island with precision and care."
 *   align="center"
 * />
 */
export const SectionHeading: React.FC<SectionHeadingProps> = ({
  eyebrow,
  heading,
  subtitle,
  align = 'left',
  dark = false,
  className = '',
  as: HeadingTag = 'h2',
}) => {
  return (
    <div className={`section-heading section-heading--${align} ${dark ? 'section-heading--dark' : ''} ${className}`}>
      {eyebrow && (
        <div className="section-heading__eyebrow">
          {eyebrow}
        </div>
      )}

      <HeadingTag className="section-heading__main">
        {heading}
      </HeadingTag>

      {subtitle && (
        <p className="section-heading__subtitle">
          {subtitle}
        </p>
      )}
    </div>
  );
};

SectionHeading.displayName = 'SectionHeading';
